"use client";

import { useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { slugify } from "@2u-landing-pages/utils/formatting";
import { EdXLogoBy2U, Facebook, Instagram, LinkedIn, Reddit, Twitter, Youtube } from "@components/Logos";
import { defaultNavigation } from "@constants/footer-navigation";
import { Body, Container, Heading } from "@red-digital/bricks";
import React, { forwardRef, MutableRefObject, useCallback, useRef } from "react";
import styles from "./Footer.module.scss";
interface FooterProps {
  navigation?: {
    title: string;
    links: {
      title: string;
      href: string;
    }[];
  }[];
}

export const Footer = forwardRef<HTMLElement, FooterProps>(({ navigation = defaultNavigation, ...rest }, ref) => {
  const internalRef = useRef<HTMLElement | null>(null) as MutableRefObject<HTMLElement | null>;

  const handleIntersect = useCallback((target: HTMLElement) => {
    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: "Footer",
        position: "Footer",
        text: "Footer",
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });
  const setObserver = useCallback(
    (element: HTMLElement | null) => {
      if (element) addObserver(element);
    },
    [addObserver]
  );

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isOneTrust = event?.currentTarget?.dataset?.target === "onetrust";

    if (isOneTrust) {
      event.preventDefault();

      if (typeof window.OneTrust === "undefined") {
        console.error("Error: OneTrust is not defined");
        return;
      }

      window.OneTrust.ToggleInfoDisplay();
    }

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: event.currentTarget.href,
        location: "Footer",
        position: "Footer",
        text: event.currentTarget?.innerText || "",
      },
    });
  };

  return (
    <footer
      className={styles.footer}
      ref={(element) => {
        if (typeof ref === "function") ref(element);
        else if (ref) ref.current = element;

        setObserver(element);
        internalRef.current = element;
      }}
      {...rest}
    >
      <Container flex direction="column" lg={{ direction: "row", justifyContent: "space-between" }}>
        <a
          href="https://www.edx.org/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOnClick}
          data-name="EdxLogo"
          aria-label="edX by 2U"
        >
          <EdXLogoBy2U />
        </a>
        <ul className={styles.socialList} aria-label="Social media links">
          <li>
            <a
              href="https://www.youtube.com/edX"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on Youtube"
            >
              <Youtube style={{ transform: "translateY(-2px)" }} />
            </a>
          </li>
          <li>
            <a
              href="https://www.facebook.com/edX"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on Facebook"
            >
              <Facebook width={24} height={24} style={{ transform: "translateY(-2px)" }} />
            </a>
          </li>
          <li>
            <a
              href="https://x.com/edXOnline"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on X"
            >
              <Twitter width={24} height={24} />
            </a>
          </li>
          <li>
            <a
              href="https://www.linkedin.com/company/edx"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on LinkedIn"
            >
              <LinkedIn width={24} height={24} />
            </a>
          </li>
          <li>
            <a
              href="https://www.instagram.com/edxonline"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on LinkedIn"
            >
              <Instagram width={24} height={24} style={{ transform: "translateY(-2px)" }} />
            </a>
          </li>
          <li>
            <a
              href="https://www.reddit.com/r/edx"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOnClick}
              aria-label="edX on Reddit"
            >
              <Reddit width={24} height={24} />
            </a>
          </li>
        </ul>
      </Container>
      <Container
        className={styles.nav}
        flex
        direction="column"
        lg={{ direction: "row" }}
        role="navigation"
        aria-label="Footer navigation"
      >
        {navigation.map((section) => (
          <div key={section.title} role="region" aria-labelledby={`nav-section-${slugify(section.title)}`}>
            <Heading
              id={`nav-section-${slugify(section.title)}`}
              className={styles.navTitle}
              type="h3"
              variant="heading-xl"
            >
              <Body variant="body-md" modifier={700}>
                {section.title}
              </Body>
            </Heading>
            <ul className={styles.navList}>
              {section.links.map(({ title, ...rest }) => (
                <li key={title}>
                  <Body type="span">
                    <a {...rest} target="_blank" rel="noopener noreferrer" onClick={handleOnClick}>
                      {title}
                    </a>
                  </Body>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Container>
      <Container>
        <Body variant="caption-lg" modifier={500}>
          © 2025 edX LLC. All rights reserved. <br />| <span lang="zh">深圳市恒宇博科技有限公司</span>{" "}
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" onClick={handleOnClick}>
            <span lang="zh">粤ICP备17044299号-2</span>
          </a>
        </Body>
      </Container>
    </footer>
  );
});

Footer.displayName = "Footer";
